import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@ordr-as/icons'
import { Link, Navigate, useParams } from 'react-router-dom'
import { FailedPspNotificationDto } from '../../../../generated-api'
import JsonHighlight from 'components/JsonHighlight'
import { formatDateAndTime } from 'utils/stringUtils'
import { Button, useToast } from '@ordr-as/umami-react'
import highlightStackTrace from './HighlightStacktrace'
import { useState } from 'react'
import { usePaymentOperations } from '../context/PaymentOperationsContext'
import { extractPaymentReference, extractValue, getReason } from './PspNotificationUtils'

type FailedPspNotificationDetailsProps = {
    failedPspNotifications: FailedPspNotificationDto[]
}
export const FailedPspNotificationDetails = ({ failedPspNotifications }: FailedPspNotificationDetailsProps) => {
    const { pspNotificationId } = useParams<{ pspNotificationId: string }>()
    const notification = failedPspNotifications.find((p) => p.pspNotificationId === Number(pspNotificationId))
    const [isRetrying, setIsRetrying] = useState<boolean>()
    const [isPayloadOpen, setIsPayloadOpen] = useState(false)
    const toast = useToast()
    const { retryFailed } = usePaymentOperations()

    const retryNotification = () => {
        if (notification) {
            setIsRetrying(true)
            retryFailed([notification.pspNotificationId])

            setTimeout(() => {
                setIsRetrying(false)
            }, 30000)
        }
    }

    if (!notification) {
        return <Navigate to="/payment-operations/psp-notifications" />
    }

    const reason = getReason(notification)
    const paymentReference = extractPaymentReference(notification)
    const pspReference = extractValue(notification, 'pspReference')
    const parsedPayload = JSON.parse(notification.payload ?? '{}')

    return (
        <div>
            <div className="rounded-lg bg-brand-white shadow-lg py-4 mt-4 px-5">
                <Link
                    to="../psp-notifications"
                    className="flex items-center text-[#3b82f6] text-sm cursor-pointer pb-4"
                >
                    <ChevronLeftIcon className="mr-2" />
                    Back to list
                </Link>
                <h2 className="text-lg font-semibold pb-2">Payment Details</h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="font-semibold">PspNotificationId:</div>
                    <div>{notification.pspNotificationId}</div>
                    <div className="font-semibold">Payment reference:</div>
                    <div>
                        {paymentReference ? (
                            <Link to={`/payments/${paymentReference}`} className="text-[#3b82f6]">
                                {paymentReference}
                            </Link>
                        ) : (
                            paymentReference
                        )}
                    </div>
                    <div className="font-semibold">Psp reference:</div>
                    <div>{pspReference}</div>
                    <div className="font-semibold">Reason:</div>
                    <div>{reason}</div>
                    <div className="font-semibold">Latest failure reason:</div>
                    <div>{notification.latestFailureReason}</div>
                    <div className="font-semibold">Type:</div>
                    <div>{notification.type}</div>
                    <div className="font-semibold">Retry count:</div>
                    <div>{notification.retryCount}</div>
                    <div className="font-semibold">Notification timestamp:</div>
                    <div>{formatDateAndTime(notification.pspNotificationTimestamp)}</div>
                    <div className="font-semibold">Updated:</div>
                    <div>{formatDateAndTime(notification.updated)}</div>
                    <div></div>
                    <div>
                        <Button kind="primary" onClick={retryNotification} isDisabled={isRetrying}>
                            Retry
                        </Button>
                    </div>

                    <div className="col-span-2">
                        <div className="flex items-center gap-2 pb-2">
                            <button
                                onClick={() => setIsPayloadOpen((v) => !v)}
                                className="flex items-center gap-1 text-sm font-semibold"
                            >
                                {isPayloadOpen ? (
                                    <ChevronDownIcon className="w-4 h-4" />
                                ) : (
                                    <ChevronRightIcon className="w-4 h-4" />
                                )}
                                Payload
                            </button>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(JSON.stringify(parsedPayload, null, 2))
                                    toast.pushSuccess({ id: 'copy-payload', message: 'Payload copied to clipboard' })
                                }}
                                title="Copy JSON"
                                className="text-grey-dark hover:text-brand-primary transition-colors"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                </svg>
                            </button>
                        </div>
                        {isPayloadOpen && (
                            <div className="bg-gray-900 rounded p-3 overflow-auto max-h-[800px]">
                                <JsonHighlight object={parsedPayload} dark />
                            </div>
                        )}
                    </div>
                    <div className="col-span-2 overflow-auto max-h-[1000px]">
                        <h3 className="text-sm font-semibold pb-2">Stack trace</h3>
                        <pre className="bg-brand-accent text-xs font-mono p-3 rounded whitespace-pre-wrap break-words">
                            {highlightStackTrace(notification.stackTrace ?? undefined) || 'No stack trace available'}
                        </pre>
                    </div>
                </div>
            </div>
        </div>
    )
}
